/**
 * Meta Pixel helpers.
 *
 * Base pixel: installed unconditionally via inline <script> in index.html.
 * `window.fbq` is therefore available from page load on every visit; this
 * module only provides typed wrappers for firing standard/custom events and
 * (optionally) applying Advanced Matching for authenticated users.
 *
 * Note: there is intentionally no consent gate on `trackMetaEvent` or
 * `initMetaPixelWithIdentity` — the ads team requires the pixel to fire
 * for all visitors. The privacy policy disclosure was updated to match.
 */

import { sha256Hex } from '@/lib/hash';

let advancedMatchingApplied = false;

/**
 * Re-initialise the pixel with Advanced Matching parameters (hashed email +
 * external_id) once an authenticated identity is available. Pre-hashes
 * locally to avoid leaving plaintext PII on window or in memory longer than
 * necessary. Safe to call repeatedly with the same identity — only the first
 * call with each unique identity actually re-inits.
 *
 * No-op when window.fbq isn't loaded (e.g. the inline snippet was blocked
 * by an ad blocker) or when neither email nor externalId is provided.
 */
export async function initMetaPixelWithIdentity(opts: {
  email?: string | null;
  externalId?: string | null;
}): Promise<void> {
  if (typeof window === 'undefined') return;
  const fbq = (window as Window & { fbq?: FbqFn }).fbq;
  if (!fbq) return;

  // Pixel ID comes from the inline snippet in index.html; we need to pass
  // it again when re-initing with Advanced Matching. Read from the env so
  // the value stays in one place (and stays absent in CI / preview).
  const pixelId = import.meta.env.VITE_META_PIXEL_ID;
  if (!pixelId) return;

  const advancedMatching: Record<string, string> = {};
  const [hashedEmail, hashedExternalId] = await Promise.all([
    sha256Hex(opts.email),
    sha256Hex(opts.externalId),
  ]);
  if (hashedEmail) advancedMatching.em = hashedEmail;
  if (hashedExternalId) advancedMatching.external_id = hashedExternalId;
  if (Object.keys(advancedMatching).length === 0) return;

  fbq('init', pixelId, advancedMatching);
  advancedMatchingApplied = true;
}

/**
 * Fire a Meta standard or custom event. No-op if the pixel base snippet
 * hasn't loaded (ad blocker, network issue). Optional eventId enables
 * dedup against a matching server-side CAPI event (used by PR #2).
 */
export function trackMetaEvent(
  name: string,
  params: Record<string, unknown> = {},
  eventId?: string,
): void {
  if (typeof window === 'undefined') return;
  const fbq = (window as Window & { fbq?: FbqFn }).fbq;
  if (!fbq) return;

  if (eventId) {
    fbq('track', name, params, { eventID: eventId });
  } else {
    fbq('track', name, params);
  }
}

/**
 * Reset module-internal state. TEST-ONLY — used by Vitest to isolate tests.
 */
export function __resetMetaPixelForTests(): void {
  advancedMatchingApplied = false;
}

// --- Types ------------------------------------------------------------------

type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
};
